import { getCookie, setCookie } from '#pplx/frontend/cookies/provider.js';
export const PPLX_METADATA_COOKIE = 'pplx.metadata';
// https://developer.chrome.com/blog/cookie-max-age-expires
const MAX_DAYS = 400;
// We cannot import from lodash-es into this directory due to NextJS edge limitation
const memoize = (fn) => {
    const cache = new Map();
    return (...args) => {
        const key = JSON.stringify(args);
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = fn(...args);
        cache.set(key, result);
        return result;
    };
};
const DEFAULT_METADATA = {
    qc: 0,
    qcu: 0,
    qcm: 0,
    qcc: 0,
    qcco: 0,
    qccol: 0,
    qcdr: 0,
    qcs: 0,
    qcg: 0,
    qcd: 0,
    hli: false,
    hcga: false,
    hcds: false,
    hso: false,
    hfo: false,
    hsco: false,
    hfco: false,
    hsma: false,
    hdc: false,
    sc: 0,
};
const getValidatedMetadataFromCookie = memoize((cookie) => {
    if (!cookie) {
        return DEFAULT_METADATA;
    }
    try {
        const parsed = JSON.parse(cookie);
        return parsed
            ? {
                ...DEFAULT_METADATA,
                ...parsed,
            }
            : DEFAULT_METADATA;
    }
    catch {
        return DEFAULT_METADATA;
    }
});
const getAndValidateMetadata = () => getValidatedMetadataFromCookie(getCookie(PPLX_METADATA_COOKIE));
const setVisitorMetadata = (metadata) => {
    setCookie(PPLX_METADATA_COOKIE, JSON.stringify(metadata), {
        expires: MAX_DAYS,
    });
};
export const incrementCookieLifetimeQueryCount = () => {
    const metadata = getAndValidateMetadata();
    metadata.qc += 1;
    setVisitorMetadata(metadata);
};
export const incrementCookieDeepResearchQueryCount = () => {
    const metadata = getAndValidateMetadata();
    metadata.qcdr += 1;
    setVisitorMetadata(metadata);
};
export const incrementCookieComputerQueryCount = () => {
    const metadata = getAndValidateMetadata();
    metadata.qcg += 1;
    setVisitorMetadata(metadata);
};
export const incrementCometQueryCount = (isLoggedIn) => {
    const metadata = getAndValidateMetadata();
    metadata.qcco += 1;
    if (isLoggedIn) {
        metadata.qccol += 1;
    }
    setVisitorMetadata(metadata);
};
export const getCookieQueryCountVisitor = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qc;
};
export const getCookieComputerQueryCount = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qcg;
};
export const getCookieCometQueryCount = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qcco;
};
export const getCookieCometQueryCountLoggedIn = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qccol;
};
export const incrementCookieDefaultSearchQueryCount = () => {
    const metadata = getAndValidateMetadata();
    metadata.qcd += 1;
    setVisitorMetadata(metadata);
};
export const updateCookieUserSettings = ({ queryCount, queryCountCopilot, queryCountMobile, }) => {
    const metadata = getAndValidateMetadata();
    metadata.qcu = queryCount;
    metadata.qcc = queryCountCopilot;
    metadata.qcm = queryCountMobile;
    setVisitorMetadata(metadata);
};
export const setCookieHasPreviouslyLoggedIn = () => {
    const metadata = getAndValidateMetadata();
    if (metadata.hli) {
        return;
    }
    metadata.hli = true;
    setVisitorMetadata(metadata);
};
export const setCookieHasClickedGetApp = () => {
    const metadata = getAndValidateMetadata();
    if (metadata.hcga) {
        return;
    }
    metadata.hcga = true;
    setVisitorMetadata(metadata);
};
export const getCookieHasClickedGetApp = () => {
    const metadata = getAndValidateMetadata();
    return metadata.hcga;
};
export const setCookieOnboarding = ({ finished } = { finished: false }) => {
    const metadata = getAndValidateMetadata();
    if (metadata.hso && metadata.hfo) {
        return;
    }
    metadata.hso = true;
    if (finished) {
        metadata.hfo = true;
    }
    setVisitorMetadata(metadata);
};
export const setCometCookieOnboarding = ({ finished } = { finished: false }) => {
    const metadata = getAndValidateMetadata();
    if (metadata.hsco && metadata.hfco) {
        return;
    }
    metadata.hsco = true;
    if (finished) {
        metadata.hfco = true;
    }
    setVisitorMetadata(metadata);
};
export const getHasLoggedIn = () => {
    const metadata = getAndValidateMetadata();
    return metadata.hli;
};
export const getHasUserQueries = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qcu > 0;
};
export const getQueryCountVisitorId = () => {
    const metadata = getAndValidateMetadata();
    return metadata.qc;
};
const RESURRECTED_ACTIVATION_DAYS = 7;
/**
 * Sets first query timestamp and last query timestamp.
 * Upon query, if last timestamp is older than 7 days, execute onResurrectedActivation.
 *
 * @param onResurrectedActivation - Callback to execute if last query is older than 7 days.
 */
export const setQueryTimestamps = (onResurrectedActivation) => {
    const metadata = getAndValidateMetadata();
    const now = Date.now();
    if (!metadata.fqa) {
        metadata.fqa = now;
    }
    const isResurrectedActivation = metadata.lqa && now - metadata.lqa > RESURRECTED_ACTIVATION_DAYS * 24 * 60 * 60 * 1000;
    metadata.lqa = now;
    setVisitorMetadata(metadata);
    if (isResurrectedActivation && onResurrectedActivation) {
        onResurrectedActivation();
    }
};
export const setCookieHasDownloadedComet = () => {
    const metadata = getAndValidateMetadata();
    if (metadata.hdc) {
        return;
    }
    metadata.hdc = true;
    setVisitorMetadata(metadata);
};
export const getCookieHasDownloadedComet = () => {
    const metadata = getAndValidateMetadata();
    return !!metadata.hdc;
};
//# sourceMappingURL=metadataCookieManager.js.map